import { WidgetShowcase } from '../WidgetShowcase.js';
import { DEMO_REVIEWS } from '../../content/demo-reviews.js';
import { useFitZoom } from '../../lib/use-fit-zoom.js';
import { SitePanel } from './Panel.js';

/**
 * The end result: reviews on a real-looking website.
 *
 * ===========================================================================
 * One example, no controls
 * ===========================================================================
 * This is the payoff of the whole page, and its job is to make somebody think
 * "I want that on my site" — not to teach them the eight layouts, and
 * definitely not to hand them a settings panel. Layout choice, colours and
 * everything else belong in the free builder, which is where the call to
 * action underneath sends them.
 *
 * So: one layout, chosen and fixed. A masonry wall, because it is the layout
 * that looks most like a considered piece of a website rather than a component
 * dropped into one, and it carries reviews of uneven length without leaving
 * gaps — which is what real reviews are. It is also visibly not the grid the
 * Manage section shows, which matters: two sections in a row drawing the same
 * layout would read as one demonstration repeated.
 *
 * ===========================================================================
 * The chrome says "somebody else's website" out loud
 * ===========================================================================
 * The frame is `SitePanel`, which the Import and Manage panels above are
 * deliberately not — they carry the ReviewsJet mark, this carries an address
 * bar with a domain in it that is not reviewsjet.com. See the note in
 * `Panel.tsx`: telling those two things apart is the single most important
 * thing the middle of this page has to communicate, and it was previously left
 * to a reader noticing that one rectangle had a nav bar in it.
 *
 * ===========================================================================
 * Why it is a hotel
 * ===========================================================================
 * A specific business is more convincing than a neutral one. "Guests" and
 * "stayed with us" and Tripadvisor marks on the cards give the section a
 * context a visitor can map onto their own, and it costs nothing but choosing.
 * A generic "Company · What our customers say" reads as a template, which is
 * the exact impression this section exists to avoid.
 *
 * The reviews are the Tripadvisor demo set — written for a hotel, so the words
 * on the cards match the page they are sitting on.
 */
/**
 * Four: two with a photograph, two without.
 *
 * Three filled exactly one row, which was tidy and slightly defeated the point
 * — a row of three is a row, and masonry only looks like masonry when a column
 * carries more than one card. The fourth review lands under the shortest column
 * and is deliberately one of the text-only ones (`PHOTO_POOL` attaches photos to
 * even indices), so the wall has two tall photo cards and two short text cards
 * to pack rather than four of a kind.
 *
 * The count is now purely a composition decision. It used to be doing a second
 * job — staying under the renderer's old six-review threshold, above which a
 * preview drew a decorative "Load more reviews" pill that did nothing. That
 * guess is gone from the renderer: the control appears only when there is a
 * page to fetch, which on a marketing page is never.
 */
const SHOWN_REVIEWS = 4;

export function DisplayResult() {
  const reviews = (DEMO_REVIEWS.tripadvisor ?? []).slice(0, SHOWN_REVIEWS);

  const { ref, zoom, canvas, isNarrow } = useFitZoom({
    wide: 1100,
    narrow: 480,
  });

  return (
    <div ref={ref} className="overflow-hidden rounded-t-2xl">
      <div style={{ width: canvas, zoom }}>
        <SitePanel
          domain="ashcombehouse.co.uk/reviews"
          name="Ashcombe House"
          nav={['Rooms', 'Dining', 'The garden']}
          cta="Book"
        >
          <div className={isNarrow ? 'px-6 py-10' : 'px-10 py-16'}>
            <div className="max-w-text">
              <p className="eyebrow text-ink-400">Guest reviews</p>
              <p className="mt-3 text-display-sm text-ink-950 sm:text-display">
                People who have stayed with us
              </p>
            </div>

            <div className="mt-10">
              {/*
            The real renderer, with its defaults. Not configured, not themed to
            match this page — what a customer gets before they change anything,
            which is the honest version of "this is what it looks like".
          */}
              <WidgetShowcase type="masonry" reviews={reviews} />
            </div>
          </div>
        </SitePanel>
      </div>
    </div>
  );
}
